import {useRef} from 'react';

import {
    Alert,
    Stack,
} from '@mantine/core';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import {DataTableColumn} from 'mantine-datatable';
import {useParams} from 'react-router-dom';

import {useAccountApplications} from '../hooks/useAccountApplications.ts';
import {useAccountApplicationsSearch} from '../hooks/useAccountApplicationsSearch.ts';
import {useAccountApplicationsSelection} from '../hooks/useAccountApplicationsSelection.ts';
import {useAccountApplicationsTableColumns} from '../hooks/useAccountApplicationsTableColumns.tsx';
import {rbacActions} from '../../../config/rbac/actions.ts';
import {useVerifiedPermissions} from '../../../hooks/useVerifiedPermissions.ts';
import {
    AccountApplication,
    AccountApplicationStatus,
} from '../types';
import classes from './RecordsTable.module.css';


export const RecordsTable = () => {

    const {id: accountId} = useParams()
    if (!accountId) {
        throw new Error('accountId is required parameter')
    }

    const {allowedActions} = useVerifiedPermissions()

    const {
        // The full loaded set BEFORE keyword filtering — the window the
        // footer's match count is measured against.
        items,
        visibleItems: records,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingNextPage,
        nextToken,
        fetchNextPage,
    } = useAccountApplications(accountId);

    const {
        columns,
    } = useAccountApplicationsTableColumns(accountId);

    // Same module-scoped store the list hook filters on — this component owns
    // the input, useAccountApplications owns the filtering.
    const {
        keyword,
        handleFilterByKeyword,
    } = useAccountApplicationsSearch();

    const {
        selection,
        handleClearSelection,
        handleRowSelection,
    } = useAccountApplicationsSelection();

    // Checkboxes appear only for an operator who can actually delete. Offering
    // selection to someone with no action to apply to it is a dead affordance.
    let selectedRecords: AccountApplication[] = [];
    if (allowedActions.includes(rbacActions.DeleteAccountApplication)) {
        selectedRecords = records.filter(record => selection[record.id] === true);
    }

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // Two distinct empty states: nothing exists yet, versus nothing matched the
    // search. Collapsing them into one message tells an operator who mistyped a
    // keyword that this account has no applications at all.
    const noRecordsText = keyword
        ? `No applications match "${keyword}" in the records loaded so far.`
        : 'No applications assigned to this account yet.';

    return (
        <Stack>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title='Could not load account applications'>
                    The console API did not return the list. Try Reload; if it persists the
                    API may be unavailable in this environment.
                </Alert>
            )}

            <MantineDataTable
                columns={columns as DataTableColumn[]}
                records={records}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={(items) => {
                    handleClearSelection();
                    items.forEach(item => {
                        handleRowSelection((item as unknown as AccountApplication).id, true)
                    })
                }}
                // Sorting is client-side over the pages loaded so far — the same
                // scope the keyword filter works on. Token pagination cannot
                // reorder rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                onScrollToBottom={() => fetchNextPage()}
                scrollViewportRef={scrollViewportRef}
                rowClassName={(record) => {
                    if (record.accountApplicationStatus === AccountApplicationStatus.ACTIVE) {
                        return classes.active;
                    } else if (record.accountApplicationStatus === AccountApplicationStatus.INACTIVE) {
                        return classes.inactive;
                    }
                }}
            />

            <DataTableTokenPagination
                loadedCount={records.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                isFetchingMore={isFetchingNextPage}
                nextToken={nextToken}
                handleLoadMore={fetchNextPage}
                noun='application'
            />

        </Stack>
    );
};
